import LogRocket from 'logrocket'
import { createPath } from 'react-router-dom'
import { StreamChat } from 'stream-chat'

import { invokeErrorPromise } from './app/utilities'

import {
  BASE_API_REQUEST_HEADER,
  HTTP_ACCEPTED,
  HTTP_CREATED,
  HTTP_INTERNAL_SERVER_ERROR,
  HTTP_LOCKED,
  HTTP_NO_CONTENT,
  HTTP_OK,
  HTTP_UNAUTHORIZED,
  METHOD_DELETE,
  METHOD_POST,
  RESOURCE_LOCATION_FILTER
} from './arch/constants'

import { clearAllDrafts } from './chat/chatService'
import { emitNativeError, withRetry } from './common'
import { HIDE_ANNOUNCEMENTS_WIDGET_KEY } from './constants'
import { clearIepReviewState } from './domain/iepReview'
import { LOGROCKET_APP_ID, STREAM_CHAT_API_KEY } from './env'
import model from './model'

import {
  trackTokenRefresh,
  trackTokenRejection,
  trackUserSignedIn,
  trackUserIdentification,
  trackGroupIdentification
} from './tracking/segment'

import { cachePostTypes, getCategoryTags, getDiagnosisTags } from './typeUtilities'

const currentChatClient = StreamChat.getInstance(STREAM_CHAT_API_KEY, {
  timeout: 10000
})

let currentUser = null
let currentToken = null
let forbiddenErrorPromise = null
let forbiddenErrorPromiseProvider = null
let lockedErrorPromise = null
let lockedErrorPromiseProvider = null
let tokenTimeout = null
let openVerifyOtpDialogCallback = null

let api = 'https://misconfigured-app.com/'

const setVerifyOtpRequestCallback = callback => (openVerifyOtpDialogCallback = callback)

const getPersistedToken = () => window.localStorage.getItem(api)
const setPersistedToken = token => window.localStorage.setItem(api, token)

const clearTokenTimeout = () => {
  if (tokenTimeout) {
    clearTimeout(tokenTimeout)
  }
}

const clearPersistedToken = () => {
  window.localStorage.removeItem(api)
  clearTokenTimeout()
}

const bootstrapUser = async token => {
  setCurrentToken(token)
  currentChatClient.disconnectUser()

  await Promise.all([cachePostTypes(), getCategoryTags(), getDiagnosisTags()])

  const user = await getMe()
  currentUser = model.user(user)

  const chatUser = user.chatToken
    ? await currentChatClient.connectUser(
        {
          id: user.username,
          name: user.name
        },
        user.chatToken
      )
    : null

  // A missing chat user may not fail here, but it will likely fail loudly later.
  currentUser.chatState = chatUser?.me
}

const apiHost = async host => {
  api = host
  const persistedToken = getPersistedToken()
  if (persistedToken) {
    try {
      await bootstrapUser(persistedToken)

      // Refresh the token immediately so that the user gets a fresh “clock.”
      await refreshToken()
    } catch (error) {
      // If the bootstrap didn’t work, then we act as if there is no token.
      currentToken = null
      clearPersistedToken()
    }
  }
}

const urlFor = resource => `${api}${resource}`

// These are somewhat arbitrary so are certainly subject to tuning, as needed.
const RETRY_COUNT = 4
const RETRY_MINIMUM = 1024
const RETRY_RANGE = 8192
const AUTHORIZED_RETRY_DELAYS_MS = Array(RETRY_COUNT).fill(RETRY_MINIMUM)

const FEATURES_RETRY_DELAYS_MS = [1000, 2000]
const FEATURES_RETRY_JITTER_MS = 500
const FEATURES_TIMEOUT_MS = 10000

const TOKEN_TIMEOUT_LENGTH = 20 * 60 * 1000 // Just under the underlying 30-minute timeout

const setForbiddenErrorPromiseProvider = callback => (forbiddenErrorPromiseProvider = callback)
const setLockedErrorPromiseProvider = callback => (lockedErrorPromiseProvider = callback)

const chatClient = () => currentChatClient
const user = () => currentUser

const navigatorChatChannel = householdId =>
  currentChatClient.channel('messaging', householdId, {
    name: 'Navigator Messenger'
  })

const goalChatChannel = goalId =>
  currentChatClient.channel('messaging', goalId, {
    name: 'Goal Discussion'
  })

const andyChatChannel = userId =>
  currentChatClient.channel('messaging', userId, {
    name: 'Andy AI'
  })

// These are now legacy wrappers for the User methods in the model module; can eventually clean out once the code base
// has transitioned over.
const userRole = acceptableRoles => {
  if (!currentUser) {
    return false
  }

  return currentUser.role(acceptableRoles)
}

const userIsInternal = () => {
  if (!currentUser) {
    return false
  }

  return currentUser.isInternal()
}

const userIsParentOf = clientId => {
  if (!currentUser) {
    return false
  }

  return currentUser.isParentOf(clientId)
}

// Navigators are allowed to access non-children.
const userChild = clientId => {
  if (!currentUser) {
    return false
  }

  return currentUser.canPlanFor(clientId)
}

/**
 * Response checks: each resolves to the response when its status is one the resource promises, and
 * throws a response error (the `Response` is attached as `error.response`) otherwise.
 */
const throwResponseError = response => {
  const error = new Error(response.statusText)
  error.response = response
  throw error
}

const statusCheck = successStatuses => async response => {
  if (successStatuses.includes(response.status)) {
    return response
  } else {
    throwResponseError(response)
  }
}

const okCheck = statusCheck([HTTP_OK])
const createdCheck = statusCheck([HTTP_CREATED])
const acceptedCheck = statusCheck([HTTP_ACCEPTED])
const noContentCheck = statusCheck([HTTP_NO_CONTENT])
const upsertCheck = statusCheck([HTTP_OK, HTTP_CREATED])

const tokenlessRequestErrorHandler = error => {
  if (!error.response) {
    return error
  } else {
    throwResponseError(error.response)
  }
}

// A POST that carries no bearer token (signups and the like), checked against the status the resource promises.
const tokenlessPost = (resource, payload, check) =>
  fetch(urlFor(resource), {
    method: METHOD_POST,
    headers: BASE_API_REQUEST_HEADER,
    body: JSON.stringify(payload)
  }).then(check, emitNativeError)

// Most token-less POSTs want the created body back, with failures surfaced as response errors.
const tokenlessPostJson = (resource, payload, check = createdCheck) =>
  tokenlessPost(resource, payload, check)
    .then(response => response.json())
    .catch(tokenlessRequestErrorHandler)

const signup = ({ name, email, clientName, tier, navigator }) =>
  tokenlessPostJson('signups', { name, email, clientName, tier, navigator })

const getSignupPrefill = key =>
  fetch(urlFor(`signups/${key}/prefill`), {
    headers: BASE_API_REQUEST_HEADER
  })
    .then(okCheck, emitNativeError)
    .then(response => response.json())
    .catch(tokenlessRequestErrorHandler)

const confirmSignup = (key, confirmationData) => tokenlessPostJson(`signups/${key}`, confirmationData)

const confirmInvitation = (key, payload) => tokenlessPostJson(`invitations/${key}`, payload)

const inviteeKnown = key =>
  fetch(urlFor(`invitations/${key}/inviteeknown`))
    .then(okCheck, emitNativeError)
    .then(response => response.json())
    .catch(tokenlessRequestErrorHandler)

// A request that hangs is given up on like one that fails, so nothing waiting on the flags waits forever.
const getFeatures = () => {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), FEATURES_TIMEOUT_MS)

  return fetch(urlFor(''), { signal: controller.signal })
    .then(okCheck)
    .then(response => response.json())
    .then(({ features }) => features ?? [])
    .finally(() => clearTimeout(timeout))
}

// A client error won’t change on a retry, whereas a network failure or a server error might.
const isRetryableFeaturesError = error => !error.response || error.response.status >= HTTP_INTERNAL_SERVER_ERROR

const getFeaturesWithRetry = () =>
  withRetry(getFeatures, {
    delays: FEATURES_RETRY_DELAYS_MS,
    jitter: FEATURES_RETRY_JITTER_MS,
    shouldRetry: isRetryableFeaturesError
  })

const requestPasswordReset = email =>
  fetch(urlFor('passwordresetters'), {
    method: METHOD_POST,
    headers: BASE_API_REQUEST_HEADER,
    body: JSON.stringify({ email })
  })
    .then(okCheck, emitNativeError)
    .then(response => response.json())
    .catch(tokenlessRequestErrorHandler)

const confirmPasswordReset = (key, password) =>
  fetch(urlFor(`passwordresetters/${key}`), {
    method: METHOD_POST,
    headers: BASE_API_REQUEST_HEADER,
    body: JSON.stringify({ password })
  })
    .then(okCheck, emitNativeError)
    .then(response => response.json())
    .catch(tokenlessRequestErrorHandler)

const setCurrentToken = token => {
  currentToken = token
  setPersistedToken(token)
  tokenTimeout = setTimeout(refreshToken, TOKEN_TIMEOUT_LENGTH)
}

const refreshToken = async () => {
  // It’s safe to clear the current refresh token timeout here, if any, in case there’s one lingering.
  // If there isn’t any, `clearTimeout` does not complain and does no harm, but if there _is_ one,
  // this ensures that it is cleared before another one is launched (via `setCurrentToken` below).
  clearTokenTimeout()

  const tokenResponse = await fullServiceFetch('tokens/refresh', { method: METHOD_POST })
  if (tokenResponse) {
    setCurrentToken(tokenResponse.token)
  }

  trackTokenRefresh(currentUser)
}

const identifyMe = navigator => {
  const currentUser = user()
  const result = trackUserIdentification(currentUser, navigator)

  // LogRocket identification remains here because it is coupled to auth state.
  if (result) {
    const { id, identifyPayload } = result
    const logRocketAppId = `${LOGROCKET_APP_ID}`
    if (logRocketAppId !== 'disabled') {
      LogRocket.identify(id, identifyPayload)
    }
  }
}

const identifyGroup = () => {
  const currentUser = user()
  trackGroupIdentification(currentUser)
}

// Refreshes the internal currentUser from the API to keep auth state in sync.
const refreshCurrentUser = async () => {
  const updatedUser = await getMe()
  if (updatedUser) {
    currentUser = model.user(updatedUser)
    return currentUser
  } else {
    return null
  }
}

const trackUserLogin = () => {
  // This one is for Segment.
  identifyMe()
  identifyGroup()

  const { username, emailConfirmedAt } = user() ?? {} // Should always be truthy, but just in case.
  if (username && emailConfirmedAt) {
    trackUserSignedIn(username)
  }
}

const trackUserAndClient = clientId => {
  // Nothing here, for now.
}

const trackNavigator = navigator => {
  identifyMe(navigator)
}

const completeLogin = async token => {
  await bootstrapUser(token)

  // Reset the announcement widget suppression flag on each new login
  // This allows existing users to experience the flow at a relaxed pace
  // Once the announcement is dismissed, we set the flag again
  window.localStorage.removeItem(HIDE_ANNOUNCEMENTS_WIDGET_KEY)

  trackUserLogin()
}

const postForToken = async (resource, payload, onToken) => {
  const response = await fetch(urlFor(resource), {
    method: METHOD_POST,
    body: JSON.stringify(payload),
    headers: BASE_API_REQUEST_HEADER
  })

  const { status } = response

  const body = await response.json()

  if (body?.token) {
    const extras = await onToken(body)
    return { status, ...extras }
  } else {
    return { ...body, status }
  }
}

const login = (username, password) =>
  postForToken('tokens', { email: username, password }, ({ token }) => completeLogin(token))

const getGoogleAuthUrl = payload =>
  fetch(urlFor('web/auth/google/url'), {
    method: METHOD_POST,
    headers: BASE_API_REQUEST_HEADER,
    body: JSON.stringify(payload || {})
  })
    .then(okCheck, emitNativeError)
    .then(response => response.json())
    .catch(tokenlessRequestErrorHandler)

const RETURN_TO_PARAM = 'returnTo'

const serializeReturnPath = from => {
  if (typeof from === 'string') {
    return from || null
  }

  if (from && typeof from === 'object') {
    return createPath(from) || null
  }

  return null
}

const decodeOAuthState = stateParam => {
  if (!stateParam) {
    return {}
  }

  try {
    const base64 = stateParam.replace(/-/g, '+').replace(/_/g, '/')
    const stateBytes = Uint8Array.from(atob(base64), stateCharacter => stateCharacter.charCodeAt(0))
    return JSON.parse(new TextDecoder().decode(stateBytes))
  } catch (error) {
    return {}
  }
}

const loginPathWithReturnTo = location => {
  const returnPath = serializeReturnPath(location)
  return returnPath ? `/login?${new URLSearchParams({ [RETURN_TO_PARAM]: returnPath })}` : '/login'
}

const loginWithSSO = (code, state, provider) =>
  postForToken(`web/tokens/sso/${provider}`, { code, state }, async ({ token, isNewUser }) => {
    await completeLogin(token)
    return { isNewUser }
  })

const createIepReviewUpload = payload => tokenlessPostJson('signups/iep-review/uploads', payload)

const signupForIepReview = payload => postForToken('signups/iep-review', payload, ({ token }) => completeLogin(token))

const exchangeIepReviewCode = code =>
  postForToken('tokens/handoff', { code }, async ({ token, returnTo }) => {
    await completeLogin(token)
    return { returnTo }
  })

const requestIepReviewLink = payload =>
  tokenlessPost('signups/iep-review/links', payload, acceptedCheck).catch(tokenlessRequestErrorHandler)

// 'direct' means that we accept a URL instead of a resource, we don’t need authorization,
// and we return the URL directly.
//
// HEAD failures are also propagated directly to the caller because these are very specific
// cases and it’s best not to generically pass them to the catch-all native error handler.
const directHead = url => fetch(url, { method: 'HEAD' }).then(okCheck)

const authorizedFetch = async (resource, settings) => {
  // The attempt number (0 for the first try) rides along on retries so that the server can tell them apart.
  const requestOptions = attempt => {
    const requiredHeaders = {
      Authorization: `Bearer ${currentToken}`,
      ...BASE_API_REQUEST_HEADER,
      ...(attempt > 0 ? { 'X-Special-X-Retry-Count': attempt } : {})
    }

    // Funky logic is meant to make sure that our necessary headers always take priority over headers that might have
    // been included in the settings.
    const { headers: settingsHeaders } = settings ?? {}
    return {
      ...settings,
      headers: {
        ...(settingsHeaders || {}),
        ...requiredHeaders
      }
    }
  }

  // 5xx “responses” throw errors and do not actually produce a response, so a failed attempt is retried with a
  // jittered pause, whatever the cause (a network failure, 500, 502, 504, … are indistinguishable here). A request
  // that is replayed after a lock or token refresh starts over with a fresh set of retries.
  //
  // TODO Send a log message instead of (or in addition to?) throwing once the retries run out, so that these
  //      errors can be stored for further triaging.
  const authorizedResponse = () =>
    withRetry(fetchAuthorized, { delays: AUTHORIZED_RETRY_DELAYS_MS, jitter: RETRY_RANGE })

  async function fetchAuthorized(attempt) {
    const response = await fetch(urlFor(resource), requestOptions(attempt))

    if (response.status === HTTP_LOCKED) {
      const result = await invokeErrorPromise(lockedErrorPromise, lockedErrorPromiseProvider, authorizedResponse)

      if (result !== null) {
        return result
      }
    }

    if (response.status === HTTP_UNAUTHORIZED) {
      // Special exception case: an unauthorized DELETE /tokens is effectively a deleted token,
      // so we bounce right back without invoking a promise. We still return the erroneous
      // response for accuracy, but we assume that anyone who requested a DELETE /tokens
      // realizes that a forbidden error means that the caller is where they want to be anyway.
      if (settings?.method === METHOD_DELETE && resource === 'tokens') {
        return response
      }

      trackTokenRejection(currentUser)

      const result = await invokeErrorPromise(forbiddenErrorPromise, forbiddenErrorPromiseProvider, authorizedResponse)

      if (result !== null) {
        return result
      }
    }

    return response
  }

  return await authorizedResponse()
}

/**
 * checkedFetch “wraps” a fetch Promise around a function that presumably checks its status
 * code then returns the json() promise if the status code is OK.
 *
 * If the fetch Promise itself fails (e.g., CORS blockage), a native error handler is called.
 * The presumption is that failures such as this are rare and potentially transient, and the
 * native error handler displays a message without disrupting the application.
 *
 * However, this also means that application code which ultimately calls this function may get
 * resolved promises that return undefined. If this undefined result is not handled well, then
 * a second-order error will be thrown, and _that_ error will be outside the catch-all error
 * handler’s scope.
 *
 * In other words: make sure that API calls can elegantly handle undefined resolved values.
 *
 * @param {Promise} fetch
 * @param {function} responseCheck
 */
const checkedFetch = (fetch, responseCheck = okCheck) =>
  fetch.then(responseCheck, emitNativeError).then(response => response && response.json())

const fullServiceFetch = (resource, settings) => checkedFetch(authorizedFetch(resource, settings))

const get = resource => fullServiceFetch(resource)
const getMe = () => get('me').then(userContainer => userContainer.user)
const query = (resource, params) => fullServiceFetch(`${resource}?${params}`)
const del = resource => fullServiceFetch(resource, { method: METHOD_DELETE })

const patch = (resource, payload) =>
  fullServiceFetch(resource, {
    method: 'PATCH',
    body: JSON.stringify(payload)
  })

/**
 * Builds a POST helper whose success is judged by the given response check: the only thing that
 * differs between the POST variants is which status(es) count as success.
 */
const checkedPost = responseCheck => (resource, payload) =>
  checkedFetch(
    authorizedFetch(resource, {
      method: METHOD_POST,
      body: payload ? JSON.stringify(payload) : null
    }),
    responseCheck
  )

const post = checkedPost(createdCheck)
const postUpsert = checkedPost(upsertCheck)
const postSearch = checkedPost(okCheck)
const postAccepted = checkedPost(acceptedCheck)

const put = (resource, payload) =>
  fullServiceFetch(resource, {
    method: 'PUT',
    body: JSON.stringify(payload)
  })

const logout = async () => {
  let logoutResponse = {}

  try {
    logoutResponse = await del('tokens')

    // by doing this we remove any cookies that are set for the resource location filter auth that's been used for persisting the states only for current login session unless token refreshes it reverts back to user's household state as default state value
    document.cookie = `${RESOURCE_LOCATION_FILTER}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`

    // Clean up any saved chat draft messages from IndexedDB storage upon successful logout
    await clearAllDrafts()
  } catch (error) {
    // The only issue with a logout error is that we won’t know if we tried to delete a
    // proxy token. That’s OK, we’ll live…we’ll just make the app like a logout did indeed
    // happen. No matter what, the current token will expire within 30 minutes anyway.
  }

  // Make sure to clean house, then check if there is a replacement token.
  currentUser = null
  currentToken = null
  clearPersistedToken()
  clearIepReviewState()
  currentChatClient.disconnectUser()
  // Resetting the analytics to clear the user data as segement stores them in the local storage and we don't want to use the previous user's data when in join page
  // so we reset the analytics to clear the user data when user logs out
  window.analytics?.reset?.()
  identifyMe()

  if (logoutResponse.token) {
    await bootstrapUser(logoutResponse.token)
  }
}

const proxy = async username => {
  const tokenContainer = await postSearch('tokens/proxy', { for: username })
  const { token } = tokenContainer || {}
  if (token) {
    await bootstrapUser(token)
  }
}

export {
  apiHost,
  urlFor,
  chatClient,
  navigatorChatChannel,
  goalChatChannel,
  andyChatChannel,
  clearPersistedToken,
  directHead,
  authorizedFetch,
  statusCheck,
  okCheck,
  createdCheck,
  acceptedCheck,
  noContentCheck,
  upsertCheck,
  throwResponseError,
  setForbiddenErrorPromiseProvider,
  get,
  query,
  patch,
  post,
  postUpsert,
  postSearch,
  postAccepted,
  put,
  del,
  signup,
  getSignupPrefill,
  confirmSignup,
  confirmInvitation,
  inviteeKnown,
  getFeatures,
  getFeaturesWithRetry,
  requestPasswordReset,
  confirmPasswordReset,
  bootstrapUser,
  login,
  getGoogleAuthUrl,
  RETURN_TO_PARAM,
  serializeReturnPath,
  decodeOAuthState,
  loginPathWithReturnTo,
  loginWithSSO,
  createIepReviewUpload,
  signupForIepReview,
  exchangeIepReviewCode,
  requestIepReviewLink,
  logout,
  identifyMe,
  trackUserLogin,
  trackUserAndClient,
  trackNavigator,
  user,
  userChild,
  userIsInternal,
  userIsParentOf,
  userRole,
  getMe,
  proxy,
  setVerifyOtpRequestCallback,
  setLockedErrorPromiseProvider,
  openVerifyOtpDialogCallback,
  refreshCurrentUser
}
